import React, {useState, useEffect} from 'react';
import {Power, Loader2, AlertTriangle, CheckCircle} from 'lucide-react';

const ToggleSignup = () => {
    const [signupEnabled, setSignupEnabled] = useState(null); // Set initial state to null until we fetch
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(false);

    useEffect(() => {
        // Fetch the current signup status on component load
        fetchSignupStatus();
    }, []);

    const fetchSignupStatus = async () => {
        setIsLoading(true);
        setError(null);

        try {
            const accessToken = localStorage.getItem('accessToken');
            const response = await fetch('https://api.digitalaalim.com/admin/signup-status/', {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${accessToken}`,
                    'x-api-key': 'SU_b399f6776c0525a5880a86c05ef80f8fc95538b5fed1c8a6825898d60e48b804', // API key if needed
                },
            });

            if (!response.ok) {
                throw new Error(`Failed to fetch signup status: ${response.statusText}`);
            }

            const data = await response.json();
            setSignupEnabled(data.signup_enabled); // Set initial state based on fetched data
        } catch (error) {
            console.error('Error fetching signup status:', error);
            setError(error.message);
        } finally {
            setIsLoading(false);
        }
    };

    const toggleSignup = async () => {
        setIsLoading(true);
        setError(null);
        setSuccess(false);

        try {
            const accessToken = localStorage.getItem('accessToken');
            const apiUrl = `https://api.digitalaalim.com/admin/toggle-signup/?enable=${!signupEnabled}`; // API URL with toggle logic

            const response = await fetch(apiUrl, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${accessToken}`,
                    'x-api-key': 'SU_b399f6776c0525a5880a86c05ef80f8fc95538b5fed1c8a6825898d60e48b804', // API key if needed
                },
            });

            if (!response.ok) {
                throw new Error(`Failed to toggle signup: ${response.statusText}`);
            }

            setSignupEnabled(!signupEnabled); // Toggle the state after a successful response
            setSuccess(true);

            // Hide success message after 3 seconds
            setTimeout(() => {
                setSuccess(false);
            }, 3000);
        } catch (error) {
            console.error('Error toggling signup:', error);
            setError(error.message);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="space-y-4">
            {/* Success message */}
            {success && (
                <div className="bg-green-50 border-l-4 border-green-500 p-4 rounded">
                    <div className="flex items-center">
                        <CheckCircle size={20} className="text-green-500 mr-2 flex-shrink-0"/>
                        <p className="text-green-700">
                            Signup has been {signupEnabled ? 'enabled' : 'disabled'} successfully!
                        </p>
                    </div>
                </div>
            )}

            {/* Error message */}
            {error && (
                <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded">
                    <div className="flex items-center">
                        <AlertTriangle size={20} className="text-red-500 mr-2 flex-shrink-0"/>
                        <p className="text-red-700">{error}</p>
                    </div>
                </div>
            )}

            {/* Loading state */}
            {isLoading && signupEnabled === null ? (
                <div className="text-center py-6">
                    <Loader2 size={30} className="animate-spin mx-auto text-blue-600"/>
                    <p className="mt-2 text-gray-600">Loading signup status...</p>
                </div>
            ) : (
                <>
                    <div className="flex items-center justify-between bg-gray-50 p-4 rounded-lg">
                        <div>
                            <h3 className="text-lg font-medium text-gray-800">User Registration</h3>
                            <p className="text-sm text-gray-600">
                                {signupEnabled
                                    ? 'New users can currently register on the platform'
                                    : 'New user registration is currently disabled'}
                            </p>
                        </div>
                        <div className="ml-4">
                            <div
                                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                                    signupEnabled ? 'bg-green-500' : 'bg-gray-300'
                                }`}>
                                <span
                                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                                        signupEnabled ? 'translate-x-6' : 'translate-x-1'
                                    }`}/>
                            </div>
                        </div>
                    </div>

                    <button
                        onClick={toggleSignup}
                        disabled={isLoading}
                        className={`w-full flex items-center justify-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                            isLoading
                                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                : signupEnabled
                                    ? 'bg-red-600 text-white hover:bg-red-700'
                                    : 'bg-green-600 text-white hover:bg-green-700'
                        }`}
                    >
                        {isLoading ? (
                            <>
                                <Loader2 size={16} className="animate-spin"/>
                                Processing...
                            </>
                        ) : (
                            <>
                                <Power size={16}/>
                                {signupEnabled ? 'Disable Signup' : 'Enable Signup'}
                            </>
                        )}
                    </button>
                </>
            )}
        </div>
    );
};

export default ToggleSignup;